import React, { useEffect, useState } from 'react';
import { FileText, Download, Loader, Search } from 'lucide-react';

const API_CONFIG = {
  baseURL: typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : '',
  getUrl(endpoint) {
    return `${this.baseURL}${endpoint}`;
  }
};

const DocumentosPublicos = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetch(API_CONFIG.getUrl('/api/public/documentos'))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setDocuments(data);
      })
      .catch(err => console.error('Error fetching public documents:', err))
      .finally(() => setLoading(false));
  }, []);

  const filteredDocs = documents.filter(doc =>
    doc.nome_ficheiro.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (doc.descricao && doc.descricao.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (doc.versao && doc.versao.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500&display=swap');

        .dp-root {
          font-family: 'DM Sans', sans-serif;
          background: #f5f2ec;
          min-height: 100vh;
          padding: 3rem 2rem;
          position: relative;
        }

        .dp-root::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Crect width='1' height='1' fill='%23c9b99a' opacity='0.3'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }

        .dp-inner {
          position: relative;
          z-index: 1;
          max-width: 1100px;
          margin: 0 auto;
        }

        .dp-header {
          margin-bottom: 3rem;
          border-bottom: 2px solid #1a1208;
          padding-bottom: 1.5rem;
        }

        .dp-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2.2rem, 5vw, 3.5rem);
          font-weight: 900;
          color: #1a1208;
          letter-spacing: -0.02em;
          line-height: 1;
          margin: 0 0 0.25rem 0;
        }

        .dp-subtitle {
          font-size: 0.8rem;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #8a7560;
          margin-top: 0.5rem;
        }

        .dp-search-wrap {
          position: relative;
          max-width: 420px;
          margin-bottom: 2.5rem;
        }

        .dp-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #8a7560;
          width: 16px;
          height: 16px;
          pointer-events: none;
        }

        .dp-search {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.6rem;
          border: 1.5px solid #1a1208;
          border-radius: 0;
          background: transparent;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.875rem;
          color: #1a1208;
          outline: none;
          transition: background 0.2s;
          box-sizing: border-box;
        }

        .dp-search::placeholder {
          color: #b0a08a;
          letter-spacing: 0.04em;
        }

        .dp-search:focus {
          background: #fff;
        }

        .dp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5px;
        }

        .dp-card {
          background: #fff;
          padding: 2rem 1.75rem;
          display: flex;
          flex-direction: column;
          border: 1.5px solid #1a1208;
          margin: -0.75px;
          transition: background 0.25s, transform 0.2s;
          cursor: default;
          position: relative;
          overflow: hidden;
        }

        .dp-card::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: #c8a96e;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }

        .dp-card:hover {
          background: #fffcf5;
          transform: translateY(-2px);
          z-index: 1;
          box-shadow: 0 8px 32px rgba(26,18,8,0.10);
        }

        .dp-card:hover::after {
          transform: scaleX(1);
        }

        .dp-card-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1.25rem;
        }

        .dp-icon {
          width: 40px;
          height: 40px;
          background: #1a1208;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #c8a96e;
          flex-shrink: 0;
        }

        .dp-version {
          font-family: 'DM Sans', sans-serif;
          font-size: 0.65rem;
          font-weight: 500;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #8a7560;
          border: 1px solid #c9b99a;
          padding: 0.2rem 0.5rem;
          background: #f5f2ec;
        }

        .dp-card-name {
          font-family: 'Playfair Display', serif;
          font-size: 1.05rem;
          font-weight: 700;
          color: #1a1208;
          margin: 0 0 0.75rem 0;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .dp-card-desc {
          font-size: 0.82rem;
          color: #6b5e4e;
          line-height: 1.6;
          flex: 1;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 3.9rem;
          font-weight: 300;
        }

        .dp-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #e8e0d4;
        }

        .dp-date {
          font-size: 0.72rem;
          color: #b0a08a;
          letter-spacing: 0.06em;
          font-weight: 400;
        }

        .dp-download {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.75rem;
          font-weight: 500;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #1a1208;
          text-decoration: none;
          border-bottom: 1px solid #1a1208;
          padding-bottom: 1px;
          transition: color 0.2s, border-color 0.2s;
        }

        .dp-download:hover {
          color: #c8a96e;
          border-color: #c8a96e;
        }

        .dp-empty {
          grid-column: 1 / -1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 5rem 2rem;
          border: 1.5px dashed #c9b99a;
          background: #fdfbf7;
          gap: 1rem;
        }

        .dp-empty-icon {
          color: #c9b99a;
          width: 40px;
          height: 40px;
        }

        .dp-empty-text {
          font-size: 0.85rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #b0a08a;
          font-weight: 400;
        }

        .dp-loader {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 64px;
          min-height: 200px;
        }

        .dp-loader svg {
          color: #c8a96e;
          animation: spin 1s linear infinite;
          width: 36px;
          height: 36px;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .dp-count {
          font-size: 0.72rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #b0a08a;
          margin-bottom: 1.5rem;
          font-weight: 400;
        }
      `}</style>

      <div className="dp-root">
        <div className="dp-inner">
          <div className="dp-header">
            <p className="dp-subtitle">Repositório</p>
            <h1 className="dp-title">Documentação Pública</h1>
          </div>

          <div className="dp-search-wrap">
            <Search className="dp-search-icon" />
            <input
              type="text"
              placeholder="Pesquisar documentação..."
              className="dp-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="dp-loader">
              <Loader />
            </div>
          ) : (
            <>
              {!loading && filteredDocs.length > 0 && (
                <p className="dp-count">{filteredDocs.length} documento{filteredDocs.length !== 1 ? 's' : ''} encontrado{filteredDocs.length !== 1 ? 's' : ''}</p>
              )}
              <div className="dp-grid">
                {filteredDocs.length === 0 ? (
                  <div className="dp-empty">
                    <FileText className="dp-empty-icon" />
                    <p className="dp-empty-text">Nenhum documento encontrado</p>
                  </div>
                ) : (
                  filteredDocs.map((doc) => (
                    <div key={doc.id} className="dp-card">
                      <div className="dp-card-top">
                        <div className="dp-icon">
                          <FileText size={18} />
                        </div>
                        {doc.versao && (
                          <span className="dp-version">{doc.versao}</span>
                        )}
                      </div>

                      <h3 className="dp-card-name" title={doc.nome_ficheiro}>
                        {doc.nome_ficheiro}
                      </h3>

                      <p className="dp-card-desc">
                        {doc.descricao || 'Sem descrição disponível.'}
                      </p>

                      <div className="dp-card-footer">
                        <span className="dp-date">
                          {new Date(doc.data_upload).toLocaleDateString('pt-PT')}
                        </span>
                        <a
                          href={API_CONFIG.getUrl(`/api/public/documentos/${doc.id}`)}
                          className="dp-download"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download size={12} />
                          Download
                        </a>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DocumentosPublicos;
